import { createSelector } from "reselect"
import { get, map, mapValues, merge, set } from "lodash"
import { getGroupedPortfolioPositions, getPortfolioPositions, getRebalancedPortfolioPositions, getGroupedRebalancedPortfolioPositions } from "./positions"
import { getGroupedRiskContributors, getRebalancedRiskContributors, getRiskContributors, getGroupedRebalancedRiskContributors } from "./risk"
import { getGroupedRiskContributorsFactors, getRebalancedRiskContributorsFactors, getRiskContributorsFactors, getGroupedRebalancedRiskContributorsFactors } from "./risk/factors"
import { getBetaContributors, getGroupedBetaContributors, getRebalancedBetaContributors, getGroupedRebalancedBetaContributors } from "./beta"
import { getExposureContributors, getContentSetContributors, getRebalancedExposureContributors, getRebalancedContentSetContributors, getGroupedExposureContributors, getGroupedContentSetContributors, getGroupedRebalancedExposureContributors, getGroupedRebalancedContentSetContributors } from "./exposure"
import { getForecastContributors, getGroupedForecastContributors, getRebalancedForecastContributors, getGroupedRebalancedForecastContributors } from "./forecast"
import { getGroupedPredictedFactorSensitivityContributors, getPredictedFactorSensitivityContributors, getRebalancedPredictedFactorSensitivityContributors, getGroupedRebalancedPredictedFactorSensitivityContributors } from "./factorSensitivity/predicted"
import { getGroupedHistoricalFactorSensitivityContributors, getHistoricalFactorSensitivityContributors, getRebalancedHistoricalFactorSensitivityContributors, getGroupedRebalancedHistoricalFactorSensitivityContributors } from "./factorSensitivity/historical"
import { getMarginalContributors, getRebalancedMarginalContributors, getGroupedMarginalContributors, getGroupedRebalancedMarginalContributors } from "./risk/marginal"
import { isUnmodeled } from "helpers/securities"

export const getContributors = createSelector(
  [
    getPortfolioPositions,
    getRiskContributors,
    getRiskContributorsFactors,
    getBetaContributors,
    getExposureContributors,
    getContentSetContributors,
    getForecastContributors,
    getHistoricalFactorSensitivityContributors,
    getPredictedFactorSensitivityContributors,
    getMarginalContributors,
  ],
  computeContributors,
)

export const getGroupedContributors = createSelector(
  [
    getGroupedPortfolioPositions,
    getGroupedRiskContributors,
    getGroupedRiskContributorsFactors,
    getGroupedBetaContributors,
    getGroupedExposureContributors,
    getGroupedContentSetContributors,
    getGroupedForecastContributors,
    getGroupedHistoricalFactorSensitivityContributors,
    getGroupedPredictedFactorSensitivityContributors,
    getGroupedMarginalContributors,
  ],
  computeContributors,
)

export const getRebalancedContributors = createSelector(
  [
    getRebalancedPortfolioPositions,
    getRebalancedRiskContributors,
    getRebalancedRiskContributorsFactors,
    getRebalancedBetaContributors,
    getRebalancedExposureContributors,
    getRebalancedContentSetContributors,
    getRebalancedForecastContributors,
    getRebalancedHistoricalFactorSensitivityContributors,
    getRebalancedPredictedFactorSensitivityContributors,
    getRebalancedMarginalContributors,
  ],
  computeContributors,
)

export const getGroupedRebalancedContributors = createSelector(
  [
    getGroupedRebalancedPortfolioPositions,
    getGroupedRebalancedRiskContributors,
    getGroupedRebalancedRiskContributorsFactors,
    getGroupedRebalancedBetaContributors,
    getGroupedRebalancedExposureContributors,
    getGroupedRebalancedContentSetContributors,
    getGroupedRebalancedForecastContributors,
    getGroupedRebalancedHistoricalFactorSensitivityContributors,
    getGroupedRebalancedPredictedFactorSensitivityContributors,
    getGroupedRebalancedMarginalContributors,
  ],
  computeContributors,
)

function computeContributors(positions, risk, riskFactors, beta, exposure, contentSet, forecast, historicalFactorSensitivity, predictedFactorSensitivity, marginalContribution) {
  const contributors = mapValues(positions, (position, id) => {
    return computeContributor(position, risk, id, riskFactors, beta, exposure, forecast, predictedFactorSensitivity, historicalFactorSensitivity, marginalContribution, contentSet)
  })

  addCashOffsetContributor(contributors, risk)

  return contributors
}

function addCashOffsetContributor(contributors, riskContributors) {
  const CASH_OFFSET_ID = "CASH_OFFSET"
  const cashRiskContributor = get(riskContributors, CASH_OFFSET_ID)
  if (cashRiskContributor) {
    set(contributors, CASH_OFFSET_ID, computeContributor(cashRiskContributor, riskContributors, CASH_OFFSET_ID))
  }
}

function computeContributor(position, risk, id, riskFactors, beta, exposure, forecast, predictedFactorSensitivity, historicalFactorSensitivity, marginalContribution, contentSet) {
  return {
    ...position,
    risk: merge(get(risk, id), get(riskFactors, id)),
    beta: get(beta, id),
    exposure: get(exposure, id),
    forecast: get(forecast, id),
    predictedFactorSensitivity: get(predictedFactorSensitivity, id),
    historicalFactorSensitivity: get(historicalFactorSensitivity, id),
    marginal: get(marginalContribution, id),
    ...get(contentSet, id, {}),
    isUnmodeled: !position.isGroup && isUnmodeled(position),
    contributors: computeContributors(
      get(position, "contributors"),
      ...map([risk, riskFactors, beta, exposure, contentSet, forecast, historicalFactorSensitivity, predictedFactorSensitivity, marginalContribution], contributor => get(contributor, [id, "contributors"])),
    ),
  }
}
